import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';

import { toast } from 'sonner';
import { getIntroIntakeFormApiEndPoint } from '@shared';
import { shortFormIntakeApi } from '@client/api/waterlily';

import type { QuickPlanFormValues } from '@/features/client-quick-plan';

interface UseShortFormIntakeOptions {
  advisorId: string;
  onSuccess?: (data: ShortFormIntakeResponse) => void;
}

function calculateBirthDateFromAge(age: string): Date {
  const ageNumber = parseInt(age, 10);
  if (Number.isNaN(ageNumber) || ageNumber < 0) {
    throw new Error(`Invalid age value: ${age}`);
  }
  const today = new Date();
  const birthYear = today.getFullYear() - ageNumber;
  return new Date(birthYear, 0, 1);
}

function mapFormToRequest(
  formValues: QuickPlanFormValues,
  advisorId: string,
): ShortFormIntakeRequest {
  const genderMap: Record<string, string> = {
    male: 'Male',
    female: 'Female',
  };

  const smokerMap: Record<string, string> = {
    never: 'No',
    former: 'Yes',
    current: 'Yes',
  };

  const intakeFormUrl = getIntroIntakeFormApiEndPoint(advisorId, false);

  return {
    clientFirstName: formValues.firstName,
    clientLastName: formValues.lastName,
    clientBirthDate: calculateBirthDateFromAge(formValues.age),
    clientGenderAtBirth: genderMap[formValues.sex] ?? 'Male',
    clientSmokerEver: smokerMap[formValues.smokingStatus] ?? 'No',
    clientMaritalStatus: formValues.maritalStatus,
    clientZipCode: formValues.zipCode,
    intakeFormUrl,
  };
}

export function useShortFormIntake({ advisorId, onSuccess }: UseShortFormIntakeOptions) {
  const navigate = useNavigate();

  const mutation = useMutation({
    mutationFn: (formValues: QuickPlanFormValues) => {
      const request = mapFormToRequest(formValues, advisorId);
      return shortFormIntakeApi.create(request);
    },
    onSuccess: data => {
      toast.success('Quick plan created successfully');
      if (onSuccess) {
        onSuccess(data);
      } else {
        navigate(`/advisors/${advisorId}/clients`);
      }
    },
    onError: (error: unknown) => {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to create quick plan. Please try again.';
      toast.error(errorMessage);
    },
  });

  return {
    submitQuickPlan: mutation.mutate,
    isPending: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error,
    data: mutation.data,
    reset: mutation.reset,
  };
}
